import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { fetchQuery, getEntry, makeKey, subscribe } from '../lib/queryCache';

export const STALE = { short: 30 * 1000, medium: 5 * 60 * 1000, long: 30 * 60 * 1000 };

/**
 * States:
 *  isLoading  - no usable data yet (show skeleton)
 *  isFetching - request in flight (background refresh if data exists)
 *  error      - last request failed (data may still hold previous valid value)
 */
export const useQuery = (name, params, fetcher, { staleTime = STALE.medium, enabled = true } = {}) => {
  const { user } = useAuth();
  const userId = user?.id;
  const key = userId ? makeKey(userId, name, params ?? null) : null;
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const entry = useSyncExternalStore(subscribe, () => (key ? getEntry(key) : undefined));
  const invalidated = entry?.invalidated;
  const hasData = entry?.data !== undefined;

  const run = useCallback((force) => {
    if (!key) return Promise.resolve();
    return fetchQuery(key, () => fetcherRef.current(), { staleTime, force }).catch(() => {});
  }, [key, staleTime]);

  // Fetch on mount / key change / invalidation (serves cache first when fresh)
  useEffect(() => {
    if (enabled && key && !entry?.promise && !(entry?.error && !hasData && !invalidated)) run(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, invalidated]);

  const fetching = !!entry?.promise;
  const error = entry?.error || null;
  return {
    data: entry?.data,
    error,
    isLoading: enabled && !!key && !hasData && !error,
    isFetching: fetching,
    isRefreshing: fetching && hasData,
    refetch: () => run(true),
  };
};
